import { computed } from 'vue';

export function useYearlyComparison(yearlyData, sortedSelectedYears) {
  const formatNumber = (num) => {
    if (num == null) return '-';
    return num.toLocaleString('en-US');
  };

  const buildMetricRow = (label, icon, iconColor, getter, higherIsBetter = true) => {
    const years = sortedSelectedYears.value;
    const values = years.map((y) => {
      const d = yearlyData.value[y];
      return d ? getter(d) : 0;
    });

    const bestVal = higherIsBetter ? Math.max(...values) : Math.min(...values);
    const bestIdx = values.indexOf(bestVal);

    const diffs = values.map((val, idx) => {
      if (idx === 0) return null;
      const prev = values[idx - 1];
      const diff = val - prev;
      const pct = prev > 0 ? Math.round((diff / prev) * 100) : val > 0 ? 100 : 0;
      const isPositive = diff > 0;
      const isBetter = higherIsBetter ? isPositive : !isPositive;
      return {
        text: `${isPositive ? '+' : ''}${pct}%`,
        color: diff === 0 ? 'grey' : isBetter ? 'success' : 'error',
        icon: diff === 0 ? 'mdi-minus' : isPositive ? 'mdi-arrow-up' : 'mdi-arrow-down',
      };
    });

    return { label, icon, iconColor, values, bestIdx, diffs };
  };

  const comparisonRows = computed(() => {
    if (sortedSelectedYears.value.length < 2) return [];
    return [
      buildMetricRow('Total Forms', 'mdi-file-document-multiple', 'primary', (d) => d.pending + d.completed),
      buildMetricRow('Completed', 'mdi-file-check', 'success', (d) => d.completed),
      buildMetricRow('Pending', 'mdi-file-clock', 'warning', (d) => d.pending, false),
      buildMetricRow('Product Recommends', 'mdi-tag-multiple', 'info', (d) => d.productRecommendation),
      buildMetricRow('Completion Rate (%)', 'mdi-percent', 'success', (d) => {
        const total = d.pending + d.completed;
        return total > 0 ? Math.round((d.completed / total) * 100) : 0;
      }),
      buildMetricRow('Recommend Rate (%)', 'mdi-bullseye-arrow', 'info', (d) => {
        const total = d.pending + d.completed + d.productRecommendation;
        return total > 0 ? Math.round((d.productRecommendation / total) * 100) : 0;
      }),
    ];
  });

  const verdictText = computed(() => {
    const years = sortedSelectedYears.value;
    if (years.length < 2) return '';
    const winCounts = years.map((_, idx) => comparisonRows.value.filter((r) => r.bestIdx === idx).length);
    const maxWins = Math.max(...winCounts);
    const winners = years.filter((_, idx) => winCounts[idx] === maxWins);
    if (winners.length === 1) {
      return `${winners[0]} leads in ${maxWins} of ${comparisonRows.value.length} metrics`;
    }
    return `${winners.join(' & ')} are tied at ${maxWins} metrics each`;
  });

  const bestYearIdx = computed(() => {
    const years = sortedSelectedYears.value;
    if (years.length < 2) return -1;
    const winCounts = years.map((_, idx) => comparisonRows.value.filter((r) => r.bestIdx === idx).length);
    const maxWins = Math.max(...winCounts);
    return winCounts.indexOf(maxWins);
  });

  return {
    formatNumber,
    comparisonRows,
    verdictText,
    bestYearIdx,
  };
}
